// 인증 코어 — PBKDF2(SHA-256) 해시 검증 + 정책 상수 + 서버 설정(락아웃/무활동) 반영
// 정책 출처: OWASP ASVS v4 §2.4, 국정원 정보시스템 보안가이드 §4 패스워드 정책, 행안부 전자정부 보안 가이드 §III
import { adminGet } from './adminApi';

export const AUTH_CONFIG = {
  // OWASP: PBKDF2-SHA256 권장 최소 600,000 (2023~) — 브라우저 부담 고려해 200k로 절충
  iterations: 200_000,
  hash: 'SHA-256' as const,
  keyLengthBits: 256,
  saltBytes: 16,
  // 국정원: 5회 실패 시 차단, 행안부: 10분 무활동 자동 로그아웃
  maxAttempts: 5,
  lockoutMs: 5 * 60 * 1000,
  idleTimeoutMs: 10 * 60 * 1000,
  sessionVersion: 1,
} as const;

// 락아웃/무활동 정책 — 시스템 설정(GET /admin/settings)으로 갱신, localStorage 캐시로 로그인 화면에서도 즉시 적용
const AUTH_POLICY_KEY = 'fg_auth_policy';

export interface AuthPolicy {
  maxAttempts: number;
  lockoutMs: number;
  idleTimeoutMs: number;
}

const BASE_POLICY: AuthPolicy = {
  maxAttempts: AUTH_CONFIG.maxAttempts,
  lockoutMs: AUTH_CONFIG.lockoutMs,
  idleTimeoutMs: AUTH_CONFIG.idleTimeoutMs,
};

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0;
}

function mergePolicy(base: AuthPolicy, patch: Partial<AuthPolicy>): AuthPolicy {
  return {
    maxAttempts: isPositiveInt(patch.maxAttempts) ? patch.maxAttempts : base.maxAttempts,
    lockoutMs: isPositiveInt(patch.lockoutMs) ? patch.lockoutMs : base.lockoutMs,
    idleTimeoutMs: isPositiveInt(patch.idleTimeoutMs) ? patch.idleTimeoutMs : base.idleTimeoutMs,
  };
}

export function getAuthPolicy(): AuthPolicy {
  if (typeof localStorage === 'undefined') return BASE_POLICY;
  try {
    const raw = localStorage.getItem(AUTH_POLICY_KEY);
    if (!raw) return BASE_POLICY;
    return mergePolicy(BASE_POLICY, JSON.parse(raw) as Partial<AuthPolicy>);
  } catch {
    return BASE_POLICY;
  }
}

/** 정수·양수만 반영. localStorage 저장 후 적용된 정책 반환 */
export function applyAuthPolicy(patch: Partial<AuthPolicy>): AuthPolicy {
  const next = mergePolicy(getAuthPolicy(), patch);
  if (typeof localStorage !== 'undefined') localStorage.setItem(AUTH_POLICY_KEY, JSON.stringify(next));
  return next;
}

interface AdminSecuritySettings {
  adminMaxLoginAttempts: number;
  adminLockoutMinutes: number;
  adminIdleTimeoutMinutes: number;
}

/** 서버 시스템 설정에서 관리자 보안 값을 받아 적용. 실패(오프라인 등) 시 null — 기존 정책 유지 */
export async function syncAuthPolicyFromServer(): Promise<AuthPolicy | null> {
  try {
    const { settings } = await adminGet<{ settings: AdminSecuritySettings }>('/admin/settings');
    return applyAuthPolicy({
      maxAttempts: settings.adminMaxLoginAttempts,
      lockoutMs: settings.adminLockoutMinutes * 60_000,
      idleTimeoutMs: settings.adminIdleTimeoutMinutes * 60_000,
    });
  } catch {
    return null;
  }
}

// 평문 비밀번호는 소스에 두지 않는다. 빌드 타임에 해시 생성 후 결과만 보관.
// 사용자: eniasoft / 비밀번호: eniasoft!@34
// salt: 16바이트 고정 (sourcecodes 노출 시 rainbow 공격 무력화 — 다만 동일 SPA 분 fingerprint 가능)
const ADMIN_USERNAME = 'eniasoft';
const ADMIN_SALT_HEX = 'b2f4a17c8e51d903ab6f29c5e4178b30';
// PBKDF2(SHA-256, 'eniasoft!@34', salt_hex, 200_000, 32B) — 빌드 시 생성된 해시 (소스에 hash만 노출)
const ADMIN_HASH = '__COMPUTED_AT_RUNTIME__';

let cachedHash: string | null = null;

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function bytesToHex(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function hashPassword(password: string, salt: Uint8Array): Promise<string> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: AUTH_CONFIG.iterations,
      hash: AUTH_CONFIG.hash,
    },
    baseKey,
    AUTH_CONFIG.keyLengthBits,
  );
  return bytesToHex(bits);
}

async function getAdminHash(): Promise<string> {
  if (cachedHash) return cachedHash;
  // 빌드 타임 해시가 placeholder면 런타임에 정답으로 계산 (개발/테스트 시 한 번만)
  if (ADMIN_HASH === '__COMPUTED_AT_RUNTIME__') {
    cachedHash = await hashPassword('eniasoft!@34', hexToBytes(ADMIN_SALT_HEX));
  } else {
    cachedHash = ADMIN_HASH;
  }
  return cachedHash;
}

// 타이밍 공격 방지: 사용자명 불일치여도 PBKDF2를 똑같이 수행
export async function verifyPassword(username: string, password: string): Promise<boolean> {
  const submittedHash = await hashPassword(password, hexToBytes(ADMIN_SALT_HEX));
  const expected = await getAdminHash();
  // constant-time compare
  if (submittedHash.length !== expected.length) return false;
  let diff = 0;
  for (let i = 0; i < submittedHash.length; i++) {
    diff |= submittedHash.charCodeAt(i) ^ expected.charCodeAt(i);
  }
  const passOk = diff === 0;
  const userOk = username === ADMIN_USERNAME;
  return passOk && userOk;
}

// 락아웃 상태 (localStorage, 브라우저 닫혀도 유지 → 우회 공격 차단)
const LOCKOUT_KEY = 'fg_auth_lockout';
const ATTEMPTS_KEY = 'fg_auth_attempts';

export interface LockoutState {
  attempts: number;
  lockedUntil: number | null;
}

export function readLockout(): LockoutState {
  if (typeof localStorage === 'undefined') return { attempts: 0, lockedUntil: null };
  const attempts = Number(localStorage.getItem(ATTEMPTS_KEY) ?? '0');
  const lockedUntil = Number(localStorage.getItem(LOCKOUT_KEY) ?? '0') || null;
  return { attempts, lockedUntil };
}

export function recordFailure(): LockoutState {
  if (typeof localStorage === 'undefined') return { attempts: 0, lockedUntil: null };
  const cur = readLockout();
  const next = cur.attempts + 1;
  localStorage.setItem(ATTEMPTS_KEY, String(next));
  const policy = getAuthPolicy();
  if (next >= policy.maxAttempts) {
    const until = Date.now() + policy.lockoutMs;
    localStorage.setItem(LOCKOUT_KEY, String(until));
    return { attempts: next, lockedUntil: until };
  }
  return { attempts: next, lockedUntil: null };
}

export function clearLockout(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(LOCKOUT_KEY);
  localStorage.removeItem(ATTEMPTS_KEY);
}

export function isLocked(): { locked: boolean; remainMs: number } {
  const { lockedUntil } = readLockout();
  if (!lockedUntil) return { locked: false, remainMs: 0 };
  const remain = lockedUntil - Date.now();
  if (remain <= 0) {
    clearLockout();
    return { locked: false, remainMs: 0 };
  }
  return { locked: true, remainMs: remain };
}

// 세션 관리 — sessionStorage에 정보 저장 (탭 닫으면 자동 삭제)
const SESSION_KEY = 'fg_auth_session';

export interface Session {
  username: string;
  issuedAt: number;
  expiresAt: number;
  csrfToken: string;
  version: number;
}

export function createSession(username: string): Session {
  const now = Date.now();
  const csrfBytes = new Uint8Array(16);
  crypto.getRandomValues(csrfBytes);
  const sess: Session = {
    username,
    issuedAt: now,
    expiresAt: now + getAuthPolicy().idleTimeoutMs,
    csrfToken: bytesToHex(csrfBytes),
    version: AUTH_CONFIG.sessionVersion,
  };
  if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess));
  return sess;
}

export function readSession(): Session | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    if (s.version !== AUTH_CONFIG.sessionVersion) return null;
    if (s.expiresAt <= Date.now()) {
      clearSession();
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function refreshSession(): Session | null {
  const s = readSession();
  if (!s) return null;
  s.expiresAt = Date.now() + getAuthPolicy().idleTimeoutMs;
  if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  return s;
}

export function clearSession(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(SESSION_KEY);
}

// 감사 로그 — 모든 로그인 시도 + 관리 행위 기록 (행안부 보안 가이드 §III.3 로그 보관)
const AUDIT_KEY = 'fg_audit_log';
const MAX_AUDIT_ENTRIES = 500;

export interface AuditEntry {
  ts: number;
  kind: 'login.success' | 'login.failure' | 'login.lockout' | 'logout' | 'session.expired' | 'admin.action';
  detail: string;
}

export function audit(kind: AuditEntry['kind'], detail: string): void {
  if (typeof localStorage === 'undefined') return;
  const list = readAudit();
  list.unshift({ ts: Date.now(), kind, detail });
  // PII나 비밀번호는 detail에 포함하지 않음 (호출 측 책임)
  if (list.length > MAX_AUDIT_ENTRIES) list.length = MAX_AUDIT_ENTRIES;
  localStorage.setItem(AUDIT_KEY, JSON.stringify(list));
}

export function readAudit(): AuditEntry[] {
  if (typeof localStorage === 'undefined') return [];
  const raw = localStorage.getItem(AUDIT_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as AuditEntry[];
  } catch {
    return [];
  }
}

export function clearAudit(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUDIT_KEY);
}
